import React, { useEffect, useState, useContext, useRef } from 'react'
import styled from 'styled-components'
import ModalTest from '../../../BaseModal'
import { CloseDiv, ContainerOfModal, Header, HeaderTitle, InputDiv, Label, Input, ButtonContainer, Button } from '../../../styles/modal-style'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { X, ChevronLeft, ChevronRight, Paperclip, Trash2 } from 'lucide-react'
import SelectSupplierInResource from './SelectSupplier'
import InfiniteSelect from '../../../InfiniteSelect'
import ColorField from './ColorField'
import PriceManager, { PriceItem, PriceManagerRef } from '../../../PriceManager'
import ApiService from '../../../../services/ApiService'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../ModalConfirmation'
import { useTenant } from '../../../../contexts/TenantContext'
import fileIcon from '../../../../assets/addStockModal/pages/file.svg'
import cancelIcon from '../../../../assets/addStockModal/pages/cancel.svg'
import PantoneSelect, { PantoneValue } from '../../../PantoneSelect'

const ProgressBarDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0.5rem 0 1.5rem 0;
  width: 100%;
`

const ProgressBarCircle = styled.div<{ $active?: boolean }>`
  width: 1vw;
  height: 1vw;
  border-radius: 2vw;
  background-color: ${({ $active }) => ($active ? '#0090D3' : '#C4C4C4')};
  flex-shrink: 0;
`

const ProgressBarLine = styled.div<{ $active?: boolean }>`
  flex: 1;
  height: 0.5vh;
  background-color: ${({ $active }) => ($active ? '#0090D3' : '#C4C4C4')};
`

const StepGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
`

const FileBlock = styled.div`
  display: flex;
  height: 25vh;
  justify-content: space-between;
  margin-top: 0.5rem;
`

const FileBlockDiv = styled.div`
  width: 100%;
`

const FileHeaderText = styled.p`
  font-weight: 700;
  font-size: 0.9vw;
  font-family: 'Inter', sans-serif;
  margin-bottom: 0;
`

const FileInputDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 1vh;
  border: dashed #33A6DC 2px;
  border-radius: 0.5vw;
  background-color: #E6F4FB;
  height: 16vh;
`

const FileInputLabel = styled.label`
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 1vw;
  padding: 1vh 1.5vw;
  background-color: #fff;
  color: #0090D3;
  border: 1px solid #0090D3;
  border-radius: 1.5vw;
  cursor: pointer;

  &.down {
    position: relative;
    top: 1.5vh;
  }
`

const FileInputHidden = styled.input`
  display: none;
`

const FileFirstText = styled.p`
  font-size: 0.9vw;
  margin-top: 1.5vh;
  margin-bottom: 0;
  font-family: 'Inter', sans-serif;
`

const FileSecondText = styled.p`
  color: #696969;
  font-size: 0.8vw;
  margin-top: 0.5vh;
  margin-bottom: 0;
  font-family: 'Inter', sans-serif;
`

const FilesInfoDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1vh;
  overflow-y: auto;
  max-height: 14vh;
`

const FileInfoDiv = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.5vh;
  padding: 0.5vh 0.8vw;
  border: 1px solid #D0D0D0;
  border-radius: 0.5vw;
  gap: 0.5vw;
`

const FileItemName = styled.p`
  margin: 0;
  font-size: 0.85vw;
  font-family: 'Inter', sans-serif;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const FileRemoveBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #E53E3E;
  display: flex;
  align-items: center;
  padding: 0;
  flex-shrink: 0;
`

const FileInfoImg = styled.img`
  width: 1.6vw;
  margin-left: 0.2vw;
  flex-shrink: 0;
`

const FileMiddleDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0.6vh;
  margin-left: 0.4vw;
  flex: 1;
  min-width: 0;
`

const FileUpperMiddleDiv = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const FileName = styled.p`
  margin-top: 0;
  margin-bottom: 0;
  font-size: 0.8vw;
  font-family: 'Inter', sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`

const FileProgressContainer = styled.div`
  margin-top: 0.8vh;
  width: 100%;
  height: 4px;
  background-color: #696969;
  border-radius: 2px;
`

const FileProgressFill = styled.div`
  height: 4px;
  background-color: #6ec1e4;
  border-radius: 2px;
  width: 100%;
`

const FileInfoCancelImg = styled.img`
  margin-left: 0.4vw;
  width: 1.3vw;
  cursor: pointer;
  flex-shrink: 0;
`

interface CompanyItemType {
  id: number
  companyName: string
  cnpj: string
}

interface CategoryItemType {
  id: number
  name: string
}

interface TypeItemType {
  id: number
  name: string
}

interface LookupItemType {
  id: number | string
  name: string
  pantone?: PantoneValue | null
}

interface ResourceFormType {
  name: string
  code: string
  category: string
  type: string
  description: string
  prices: PriceItem[]
  totalWeight: string
  lengthInMm: string
  unitQuantity: string
  minQuantity: string
  maxQuantity: string
  stockUnit: string
  dimension1: string
  dimension2: string
  carrierCode?: string
}

const CreateResourceModal = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const id = searchParams.get('id')
  const sourceId = searchParams.get('sourceId')
  const variation = searchParams.get('variation') === 'true'
  const { setFeedback } = useContext(FeedbackContext)
  const { isFashionTenant } = useTenant()
  const priceManagerRef = useRef<PriceManagerRef>(null)
  const currentPricesRef = useRef<PriceItem[]>([])

  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1)
  const [invoiceFiles, setInvoiceFiles] = useState<File[]>([])
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false)
  const [validationFields, setValidationFields] = useState<string[]>([])

  const [selectedColor, setSelectedColor] = useState<LookupItemType | null>(null)
  const [stockUnitTouched, setStockUnitTouched] = useState(true)
  const [originalColorName, setOriginalColorName] = useState<string | null>(null)
  const [pendingColorPantone, setPendingColorPantone] = useState<PantoneValue | null>(null)
  const [colorError, setColorError] = useState(false)
  const [pantoneError, setPantoneError] = useState(false)
  const [nameError, setNameError] = useState(false)
  const [categoryError, setCategoryError] = useState(false)
  const [materialError, setMaterialError] = useState(false)
  const [selectedSuppliers, setSelectedSuppliers] = useState<CompanyItemType[]>([])
  const [selectedCategories, setSelectedCategories] = useState<CategoryItemType[]>([])
  const [selectedTypes, setSelectedTypes] = useState<TypeItemType[]>([])
  const [selectedMaterial, setSelectedMaterial] = useState<LookupItemType | null>(null)
  const [selectedFormat, setSelectedFormat] = useState<LookupItemType | null>(null)
  const [selectedFinishing, setSelectedFinishing] = useState<LookupItemType | null>(null)
  const [selectedNcm, setSelectedNcm] = useState<LookupItemType | null>(null)
  const [selectedShelf, setSelectedShelf] = useState<LookupItemType | null>(null)
  const [selectedStockLocation, setSelectedStockLocation] = useState<LookupItemType | null>(null)
  const [selectedStockStreet, setSelectedStockStreet] = useState<LookupItemType | null>(null)
  const [previousCategoryId, setPreviousCategoryId] = useState<number | null>(null)
  const [codeError, setCodeError] = useState(false)
  const [formData, setFormData] = useState<ResourceFormType>({
    name: '',
    code: '',
    category: '',
    type: '',
    description: '',
    prices: [],
    totalWeight: '',
    lengthInMm: '',
    unitQuantity: '',
    minQuantity: '',
    maxQuantity: '',
    stockUnit: 'KG',
    dimension1: '',
    dimension2: '',
    carrierCode: '',
  })

  useEffect(() => {
    const fetchData = async () => {
      const loadId = sourceId || id
      if (loadId) {
        try {
          const response = await ApiService.resources.findById(Number(loadId))
          const data = response.data

          const { suppliers, category, type, prices, code, description, companyResource, ...rest } = data

          setFormData({
            ...rest,
            code: variation ? '' : code || '',
            description: description || '',
            prices: prices || [],
            totalWeight: rest.totalWeight != null ? String(rest.totalWeight) : '',
            lengthInMm: rest.lengthInMm != null ? String(rest.lengthInMm) : '',
            unitQuantity: rest.unitQuantity != null ? String(rest.unitQuantity) : '',
            minQuantity: rest.minQuantity != null ? String(rest.minQuantity) : '',
            maxQuantity: rest.maxQuantity != null ? String(rest.maxQuantity) : '',
            stockUnit: rest.stockUnit || (isFashionTenant ? 'KG' : ''),
            dimension1: rest.dimension1 != null ? String(rest.dimension1) : '',
            dimension2: rest.dimension2 != null ? String(rest.dimension2) : '',
            carrierCode: rest.carrierCode != null ? String(rest.carrierCode) : '',
          })
          currentPricesRef.current = prices || []
          setSelectedSuppliers(suppliers)
          if (category) {
            setSelectedCategories([category])
          }
          if (type) {
            setSelectedTypes([type])
          }
          if (rest.materialId && rest.material) {
            setSelectedMaterial({ id: rest.materialId, name: rest.material })
          }
          if (rest.formatId && rest.format) {
            setSelectedFormat({ id: rest.formatId, name: rest.format })
          }
          if (rest.finishingId && rest.finishing) {
            setSelectedFinishing({ id: rest.finishingId, name: rest.finishing })
          }
          if (rest.ncmId && rest.ncm) {
            setSelectedNcm({ id: rest.ncmId, name: rest.ncm })
          }
          if (rest.shelfId && rest.shelf) {
            setSelectedShelf({ id: rest.shelfId, name: rest.shelf })
          }
          if (rest.stockLocationId && rest.stockLocation) {
            setSelectedStockLocation({ id: rest.stockLocationId, name: rest.stockLocation })
          }
          if (rest.stockStreetId && rest.stockStreet) {
            setSelectedStockStreet({ id: rest.stockStreetId, name: rest.stockStreet })
          }
          if (data.colors && Array.isArray(data.colors) && data.colors.length > 0) {
            const firstColor = data.colors[0]
            const resolvedColorId = firstColor.itemColorId ?? firstColor.id ?? null
            const resolvedColorName = firstColor.name || firstColor?.colorName || ''
            if (resolvedColorId && resolvedColorName) {
              const colorObj: LookupItemType = {
                id: resolvedColorId,
                name: resolvedColorName,
                pantone: firstColor.pantone ?? null,
              }
              setPendingColorPantone(colorObj.pantone ?? null)
              if (variation) {
                setOriginalColorName(colorObj.name)
                setSelectedColor(null)
              } else {
                setSelectedColor(colorObj)
              }
            } else {
              setPendingColorPantone(firstColor.pantone ?? null)
            }
          }
        } catch (error) {
          console.log('Erro ao carregar dados:', error)
        }
      }
    }
    fetchData()
  }, [id, sourceId])

  useEffect(() => {
    const currentCategoryId = selectedCategories[0]?.id || null

    if (selectedCategories.length === 0) {
      setSelectedTypes([])
      setPreviousCategoryId(null)
    } else if (currentCategoryId !== previousCategoryId && previousCategoryId !== null) {
      setSelectedTypes([])
      setPreviousCategoryId(currentCategoryId)
    } else if (previousCategoryId === null) {
      setPreviousCategoryId(currentCategoryId)
    }
  }, [selectedCategories, previousCategoryId])

  const handleInputChange = (field: keyof ResourceFormType, value: string | PriceItem[]) => {
    console.log('[Resource] handleInputChange', { field, value, stockUnit: formData.stockUnit, isFashionTenant })
    if (field === 'prices' && Array.isArray(value)) {
      currentPricesRef.current = value
      setFormData(prev => ({ ...prev, prices: value }))
      return
    }

    if (field === 'stockUnit') {
      setStockUnitTouched(true)
      setFormData(prev => ({
        ...prev,
        stockUnit: value as string,
        unitQuantity: '',
        totalWeight: '',
        lengthInMm: '',
        minQuantity: '',
        maxQuantity: '',
      }))
      return
    }

    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const isResourceUnitFieldVisible = (field: 'unitQuantity' | 'totalWeight' | 'lengthInMm') => {
    if (!isFashionTenant) return true
    if (!formData.stockUnit) return false
    if (formData.stockUnit === 'KG') return field === 'totalWeight'
    if (formData.stockUnit === 'MM') return field === 'lengthInMm'
    if (formData.stockUnit === 'UN') return field === 'unitQuantity'
    return false
  }

  useEffect(() => {
    console.log('[Resource] stockUnit changed', { stockUnit: formData.stockUnit, formData })
  }, [formData.stockUnit, formData])

  const handleUploadInvoice = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setInvoiceFiles(prev => [...prev, ...files])
    e.target.value = ''
  }

  const handleRemoveInvoice = (index: number) => {
    setInvoiceFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleNext = () => {
    if (currentStep === 1) {
      const missingFields: string[] = []
      if (!formData.name.trim()) {
        setNameError(true)
        missingFields.push('Nome')
      }
      if (!selectedMaterial) {
        setMaterialError(true)
        missingFields.push('Material')
      }
      if (missingFields.length > 0) {
        setValidationFields(missingFields)
        setIsValidationModalOpen(true)
        return
      }
    }
    if (currentStep === 2 && isFashionTenant && !selectedColor) {
      setColorError(true)
      setValidationFields(['Cor'])
      setIsValidationModalOpen(true)
      return
    }
    if (currentStep === 2 && selectedColor) {
      const selectedColorPantone = selectedColor.pantone ?? pendingColorPantone
      if (!selectedColorPantone?.code) {
        setPantoneError(true)
        setValidationFields(['Pantone da cor'])
        setIsValidationModalOpen(true)
        return
      }
    }
    setCurrentStep(prev => (prev + 1) as 1 | 2 | 3)
  }

  const handleBack = () => {
    setCurrentStep(prev => (prev - 1) as 1 | 2 | 3)
  }

  const onSubmit = async () => {
    if (!isFashionTenant && (!formData.code || !formData.code.trim())) {
      setCodeError(true)
      setValidationFields(['Código'])
      setIsValidationModalOpen(true)
      return
    }

    if (isFashionTenant && !selectedColor) {
      setColorError(true)
      setValidationFields(['Cor'])
      setIsValidationModalOpen(true)
      return
    }
    if (selectedColor) {
      const selectedColorPantone = selectedColor.pantone ?? pendingColorPantone
      if (!selectedColorPantone?.code) {
        setPantoneError(true)
        setValidationFields(['Pantone da cor'])
        setIsValidationModalOpen(true)
        return
      }
    }

    if (priceManagerRef.current) {
      const priceAdded = priceManagerRef.current.addPendingPrice()
      if (priceAdded) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    }

    await new Promise(resolve => setTimeout(resolve, 50))

    try {
      const finalPrices = currentPricesRef.current.length > 0 ? currentPricesRef.current : formData.prices
      const { id: formId, companyResource, category, type, ...cleanFormData } = formData as any
      const selectedCategory = selectedCategories[0] || null
      const selectedType = selectedTypes[0] || null

      const resolvedMaterialId = selectedMaterial ? selectedMaterial.id : null

      const requestData = {
        ...cleanFormData,
        prices: finalPrices.map((price: any) => ({
          price: Number(price.price),
          unitOfMeasurementId: Number(price.unitOfMeasurementId),
        })),
        carrierCode: formData.carrierCode ? Number(formData.carrierCode) : null,
        minQuantity: formData.minQuantity !== '' ? Number(String(formData.minQuantity).replace(',', '.')) : null,
        maxQuantity: formData.maxQuantity !== '' ? Number(String(formData.maxQuantity).replace(',', '.')) : null,
        suppliers: selectedSuppliers.map(supplier => supplier.id),
        categoryId: selectedCategory ? selectedCategory.id : null,
        category: selectedCategory ? selectedCategory.name : null,
        typeId: selectedType ? selectedType.id : null,
        type: selectedType ? selectedType.name : null,
        materialId: resolvedMaterialId,
        formatId: selectedFormat ? selectedFormat.id : null,
        finishingId: selectedFinishing ? selectedFinishing.id : null,
        ncmId: selectedNcm ? selectedNcm.id : null,
        shelfId: selectedShelf ? selectedShelf.id : null,
        stockLocationId: selectedStockLocation ? selectedStockLocation.id : null,
        stockStreetId: selectedStockStreet ? selectedStockStreet.id : null,
        colors: selectedColor
          ? [{
            colorName: selectedColor.name,
            pantone: (selectedColor.pantone ?? pendingColorPantone)
              ? {
                code: (selectedColor.pantone ?? pendingColorPantone)!.code,
                name: (selectedColor.pantone ?? pendingColorPantone)!.name,
              }
              : undefined,
          }]
          : [],
      }

      const isEdit = !!id && !variation
      const action = isEdit ? 'atualizado' : variation ? 'criado como variação' : 'criado'

      let response: any = null
      if (isEdit) {
        response = await ApiService.resources.update(Number(id), requestData)
      } else {
        response = await ApiService.resources.create(requestData)
      }

      const wasCodeEmpty = !requestData.code || !requestData.code.trim()
      const finalCode = response?.data?.code || response?.data?.Code?.code

      if (!isEdit && wasCodeEmpty && isFashionTenant && finalCode) {
        setFeedback({
          message: `Código não informado, criado automaticamente com: ${finalCode}`,
          isOpen: true,
          statusOk: true,
          timeout: 20000,
        })
      } else {
        setFeedback({ message: `Insumo ${action} com sucesso!`, isOpen: true, statusOk: true })
      }
      navigate('/panel/stock/resource')
    } catch (error: any) {
      const action = id && !variation ? 'atualizar' : 'criar'
      let errorMessage = `Erro ao ${action} insumo`
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.status === 400) {
        errorMessage = 'Dados inválidos. Verifique as informações inseridas.'
      } else if (error.response?.status === 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente.'
      } else if (error.message) {
        errorMessage = error.message
      }
      setFeedback({ message: errorMessage, isOpen: true, statusOk: false })
    }
  }

  const itemName = isFashionTenant ? 'Aviamento' : 'Insumo'

  const title = variation ? 'Adicionar Variação' : id ? `Editar ${itemName.toLowerCase()}` : `Novo ${itemName}`

  return (
    <ModalTest>
      <ConfirmationModal
        isOpen={isValidationModalOpen}
        title="Campos obrigatórios não preenchidos"
        message={
          <div>
            Preencha os seguintes campos antes de continuar:
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {validationFields.map((field, i) => (
                <li key={i}>{field}</li>
              ))}
            </ul>
          </div>
        }
        actions={[{ label: 'Entendi', onClick: () => setIsValidationModalOpen(false), variant: 'primary' }]}
      />

      <ContainerOfModal width='60vw' maxHeight='90vh'>
        <Header style={{ marginBottom: '0.5rem' }}>
          <HeaderTitle>{title}</HeaderTitle>
          <CloseDiv onClick={() => navigate('/panel/stock/resource')}>
            <X size={20} />
          </CloseDiv>
        </Header>

        <ProgressBarDiv>
          <ProgressBarCircle $active={true} />
          <ProgressBarLine $active={currentStep >= 2} />
          <ProgressBarCircle $active={currentStep >= 2} />
          <ProgressBarLine $active={currentStep >= 3} />
          <ProgressBarCircle $active={currentStep >= 3} />
        </ProgressBarDiv>

        {currentStep === 1 && (
          <StepGrid>
            <InputDiv>
              <Label style={nameError ? { color: 'red' } : {}}>
                Nome
                <span style={{ color: 'red', marginLeft: 2 }}>*</span>
                {nameError && (
                  <span style={{ fontSize: '12px', color: 'red', marginLeft: 6, fontWeight: 400 }}>
                    O nome é obrigatório!
                  </span>
                )}
              </Label>
              <Input
                autoComplete='off'
                type='text'
                value={formData.name}
                onChange={e => { handleInputChange('name', e.target.value); setNameError(false) }}
                placeholder="Digite o nome do insumo"
                style={{ height: '40px', ...(nameError ? { border: '1px solid red', borderRadius: '2rem' } : {}) }}
              />
            </InputDiv>

            <InputDiv>
              <Label>
                Categoria
              </Label>
              <InfiniteSelect
                mode="crud"
                value={selectedCategories[0] || null}
                onSelect={option => {
                  setSelectedCategories(option ? [option] : [])
                  setCategoryError(false)
                }}
                endpointConfig={{
                  listPath: '/resources/resource/util/categories',
                  createPath: '/resources/resource/util/categories',
                  deletePath: '/resources/resource/util/categories',
                  headers: { 'Cache-Control': 'no-cache' },
                }}
                onItemCreate={async (name: string) => {
                  const response = await ApiService.resources.createCategory({ name })
                  return response.data
                }}
                onItemDelete={async (catId: any) => {
                  await ApiService.resources.deleteCategory(catId)
                }}
                fetchFunction={async () => {
                  const response = await ApiService.resources.getCategories({ headers: { 'Cache-Control': 'no-cache' } })
                  return response.data
                }}
                placeholder="Buscar ou criar categoria"
                initialOptions={selectedCategories}
                allowCreate={true}
                allowDelete={true}
                showCreateOnlyIfNotExists={true}
                minCreateLength={2}
              />
            </InputDiv>

            <InputDiv>
              <Label>Tipo</Label>
              <InfiniteSelect
                mode="crud"
                value={selectedTypes[0] || null}
                onSelect={option => setSelectedTypes(option ? [option] : [])}
                endpointConfig={{
                  listPath: '/resources/resource/util/types',
                  createPath: '/resources/resource/util/types',
                  deletePath: '/resources/resource/util/types',
                  headers: { 'Cache-Control': 'no-cache' },
                }}
                onItemCreate={async (name: string) => {
                  const response = await ApiService.resources.createType({ name, categoryId: selectedCategories[0]?.id })
                  return response.data
                }}
                onItemDelete={async (typeId: any) => {
                  await ApiService.resources.deleteType(typeId)
                }}
                fetchFunction={async () => {
                  const response = await ApiService.resources.getTypes({
                    params: { categoryId: selectedCategories[0]?.id },
                    headers: { 'Cache-Control': 'no-cache' },
                  })
                  return response.data
                }}
                placeholder="Buscar ou criar tipo"
                disabled={false}
                initialOptions={selectedTypes}
                allowCreate={true}
                allowDelete={true}
                showCreateOnlyIfNotExists={true}
                minCreateLength={2}
              />
            </InputDiv>

            <InputDiv>
              <Label style={materialError ? { color: 'red' } : {}}>
                Material
                <span style={{ color: 'red', marginLeft: 2 }}>*</span>
                {materialError && (
                  <span style={{ fontSize: '12px', color: 'red', marginLeft: 6, fontWeight: 400 }}>
                    O material é obrigatório!
                  </span>
                )}
              </Label>
              <div style={materialError ? { border: '1px solid red', borderRadius: '2rem', overflow: 'hidden' } : {}}>
                <InfiniteSelect
                  mode="crud"
                  value={selectedMaterial}
                  onSelect={option => { setSelectedMaterial(option); setMaterialError(false) }}
                  fetchFunction={async (limit, offset, search?) => {
                    const response = await ApiService.stockLookup.getMaterialsForResource()
                    const items = response.data || []
                    const filtered = search
                      ? items.filter((m: any) => m.name?.toLowerCase().includes(search.toLowerCase()))
                      : items
                    return filtered.map((m: any) => ({ id: m.id, name: m.name }))
                  }}
                  onItemCreate={async (name: string) => {
                    const response = await ApiService.stockLookup.createMaterial({ name: name.trim(), type: 'RESOURCE' })
                    return response.data
                  }}
                  onItemDelete={async (matId: any) => {
                    await ApiService.stockLookup.deleteMaterial(matId)
                  }}
                  placeholder="Buscar ou criar material"
                  allowCreate={true}
                  allowDelete={true}
                  showCreateOnlyIfNotExists={true}
                  minCreateLength={2}
                />
              </div>
            </InputDiv>

            <InputDiv>
              <Label>Formato</Label>
              <InfiniteSelect
                mode="crud"
                value={selectedFormat}
                onSelect={option => setSelectedFormat(option)}
                fetchFunction={async (limit, offset, search?) => {
                  const response = await ApiService.stockLookup.getFormats()
                  const items = response.data || []
                  const filtered = search
                    ? items.filter((m: any) => m.name?.toLowerCase().includes(search.toLowerCase()))
                    : items
                  return filtered.map((m: any) => ({ id: m.id, name: m.name }))
                }}
                onItemCreate={async (name: string) => {
                  const response = await ApiService.stockLookup.createFormat({ name })
                  return response.data
                }}
                onItemDelete={async (fmtId: any) => {
                  await ApiService.stockLookup.deleteFormat(fmtId)
                }}
                placeholder="Buscar ou criar formato"
                allowCreate={true}
                allowDelete={true}
                showCreateOnlyIfNotExists={true}
                minCreateLength={2}
              />
            </InputDiv>

            <InputDiv>
              <Label>Acabamento</Label>
              <InfiniteSelect
                mode="crud"
                value={selectedFinishing}
                onSelect={option => setSelectedFinishing(option)}
                fetchFunction={async (limit, offset, search?) => {
                  const response = await ApiService.stockLookup.getFinishings()
                  const items = response.data || []
                  const filtered = search
                    ? items.filter((m: any) => m.name?.toLowerCase().includes(search.toLowerCase()))
                    : items
                  return filtered.map((m: any) => ({ id: m.id, name: m.name || m.type }))
                }}
                onItemCreate={async (name: string) => {
                  const response = await ApiService.stockLookup.createFinishing({ name: name.trim() })
                  return response.data
                }}
                onItemDelete={async (finId: any) => {
                  await ApiService.stockLookup.deleteFinishing(finId)
                }}
                placeholder="Buscar ou criar acabamento"
                allowCreate={true}
                allowDelete={true}
                showCreateOnlyIfNotExists={true}
                minCreateLength={2}
              />
            </InputDiv>

            <SelectSupplierInResource suppliers={selectedSuppliers} setSuppliers={setSelectedSuppliers} />

            <InputDiv>
              <Label>Rua do Estoque</Label>
              <InfiniteSelect
                mode="crud"
                value={selectedStockStreet}
                onSelect={option => setSelectedStockStreet(option)}
                fetchFunction={async (limit, offset, search?) => {
                  const response = await ApiService.stockLookup.getStockStreets()
                  const items = response.data || []
                  const filtered = search
                    ? items.filter((m: any) => m.name?.toLowerCase().includes(search.toLowerCase()))
                    : items
                  return filtered.map((m: any) => ({ id: m.id, name: m.name }))
                }}
                onItemCreate={async (name: string) => {
                  const response = await ApiService.stockLookup.createStockStreet({ name })
                  return response.data
                }}
                onItemDelete={async (streetId: any) => {
                  await ApiService.stockLookup.deleteStockStreet(streetId)
                }}
                placeholder="Buscar ou criar rua"
                allowCreate={true}
                allowDelete={true}
                showCreateOnlyIfNotExists={true}
                minCreateLength={2}
              />
            </InputDiv>

            <InputDiv>
              <Label>Prateleira</Label>
              <InfiniteSelect
                mode="crud"
                value={selectedShelf}
                onSelect={option => setSelectedShelf(option)}
                fetchFunction={async (limit, offset, search?) => {
                  const response = await ApiService.stockLookup.getShelfs()
                  const items = response.data || []
                  const filtered = search
                    ? items.filter((m: any) => m.name?.toLowerCase().includes(search.toLowerCase()))
                    : items
                  return filtered.map((m: any) => ({ id: m.id, name: m.name }))
                }}
                onItemCreate={async (name: string) => {
                  const response = await ApiService.stockLookup.createShelf({ name })
                  return response.data
                }}
                onItemDelete={async (shelfId: any) => {
                  await ApiService.stockLookup.deleteShelf(shelfId)
                }}
                placeholder="Buscar ou criar prateleira"
                allowCreate={true}
                allowDelete={true}
                showCreateOnlyIfNotExists={true}
                minCreateLength={2}
              />
            </InputDiv>

            <InputDiv>
              <Label>Localização</Label>
              <InfiniteSelect
                mode="crud"
                value={selectedStockLocation}
                onSelect={option => setSelectedStockLocation(option)}
                fetchFunction={async (limit, offset, search?) => {
                  const response = await ApiService.stockLookup.getStockLocations()
                  const items = response.data || []
                  const filtered = search
                    ? items.filter((m: any) => m.name?.toLowerCase().includes(search.toLowerCase()))
                    : items
                  return filtered.map((m: any) => ({ id: m.id, name: m.name }))
                }}
                onItemCreate={async (name: string) => {
                  const response = await ApiService.stockLookup.createStockLocation({ name })
                  return response.data
                }}
                onItemDelete={async (locId: any) => {
                  await ApiService.stockLookup.deleteStockLocation(locId)
                }}
                placeholder="Buscar ou criar localização"
                allowCreate={true}
                allowDelete={true}
                showCreateOnlyIfNotExists={true}
                minCreateLength={2}
              />
            </InputDiv>

            <InputDiv>
              <Label>Código da Transportadora</Label>
              <Input
                autoComplete='off'
                type='number'
                value={formData.carrierCode || ''}
                onChange={e => handleInputChange('carrierCode', e.target.value)}
                placeholder="Digite o código"
                style={{ height: '40px' }}
              />
            </InputDiv>

            <InputDiv style={{ gridColumn: '1 / -1' }}>
              <Label>Descrição</Label>
              <Input
                autoComplete='off'
                type='text'
                value={formData.description}
                onChange={e => handleInputChange('description', e.target.value)}
                placeholder="Digite a descrição do insumo"
                style={{ height: '40px' }}
              />
            </InputDiv>

            <ButtonContainer>
              <Button type='button' onClick={() => navigate('/panel/stock/resource')} className='cancel'>
                Cancelar
              </Button>
              <Button
                type='button'
                onClick={handleNext}
              >
                Próximo <ChevronRight size={16} />
              </Button>
            </ButtonContainer>
          </StepGrid>
        )}

        {currentStep === 2 && (
          <StepGrid>
            <InputDiv>
              <Label>Unidade de Estoque</Label>
              <Input
                as="select"
                value={formData.stockUnit}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange('stockUnit', e.target.value)}
                style={{ height: '40px', cursor: 'pointer' }}
              >
                <option value='' hidden>Escolha</option>
                <option value='KG'>KG</option>
                <option value='MM'>MM</option>
                <option value='UN'>UN</option>
              </Input>
            </InputDiv>

            <InputDiv style={{ display: isResourceUnitFieldVisible('unitQuantity') ? 'block' : 'none' }}>
              <Label>Quantidade</Label>
              <Input
                autoComplete='off'
                type='number'
                value={formData.unitQuantity}
                onChange={e => handleInputChange('unitQuantity', e.target.value)}
                placeholder='0'
                style={{ height: '40px' }}
              />
            </InputDiv>

            <InputDiv style={{ display: isResourceUnitFieldVisible('totalWeight') ? 'block' : 'none' }}>
              <Label>Peso Total</Label>
              <Input
                autoComplete='off'
                type='number'
                value={formData.totalWeight}
                onChange={e => handleInputChange('totalWeight', e.target.value)}
                placeholder='0'
                style={{ height: '40px' }}
              />
            </InputDiv>

            <InputDiv style={{ display: isResourceUnitFieldVisible('lengthInMm') ? 'block' : 'none' }}>
              <Label>Comprimento (mm)</Label>
              <Input
                autoComplete='off'
                type='number'
                value={formData.lengthInMm}
                onChange={e => handleInputChange('lengthInMm', e.target.value)}
                placeholder='0'
                style={{ height: '40px' }}
              />
            </InputDiv>

            <InputDiv>
              <Label>Estoque mínimo{formData.stockUnit ? ` (${formData.stockUnit})` : ''}</Label>
              <Input
                autoComplete='off'
                type='number'
                value={formData.minQuantity}
                onChange={e => handleInputChange('minQuantity', e.target.value)}
                placeholder='Opcional'
                style={{ height: '40px' }}
              />
            </InputDiv>

            <InputDiv>
              <Label>Estoque máximo{formData.stockUnit ? ` (${formData.stockUnit})` : ''}</Label>
              <Input
                autoComplete='off'
                type='number'
                value={formData.maxQuantity}
                onChange={e => handleInputChange('maxQuantity', e.target.value)}
                placeholder='Opcional'
                style={{ height: '40px' }}
              />
            </InputDiv>

            <InputDiv>
              <Label>Dimensão 1</Label>
              <Input
                autoComplete='off'
                type='number'
                value={formData.dimension1}
                onChange={e => handleInputChange('dimension1', e.target.value)}
                placeholder="0"
                style={{ height: '40px' }}
              />
            </InputDiv>

            <InputDiv>
              <Label>Dimensão 2</Label>
              <Input
                autoComplete='off'
                type='number'
                value={formData.dimension2}
                onChange={e => handleInputChange('dimension2', e.target.value)}
                placeholder="0"
                style={{ height: '40px' }}
              />
            </InputDiv>

            <ColorField
              isFashionTenant={isFashionTenant}
              colorError={colorError}
              pantoneError={pantoneError}
              originalColorName={originalColorName}
              selectedColor={selectedColor}
              pendingColorPantone={pendingColorPantone}
              setSelectedColor={setSelectedColor}
              setPendingColorPantone={setPendingColorPantone}
              setColorError={setColorError}
              setPantoneError={setPantoneError}
            />

            <InputDiv style={{ gridColumn: '2 / -1' }}>
              <PriceManager
                ref={priceManagerRef}
                prices={formData.prices}
                onChange={prices => handleInputChange('prices', prices)}
                baseUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/resources` || ''}
                label="Preços"
              />
            </InputDiv>

            <ButtonContainer>
              <Button type='button' onClick={handleBack} className='cancel'>
                <ChevronLeft size={16} /> Voltar
              </Button>
              <Button type='button' onClick={handleNext}>
                Próximo <ChevronRight size={16} />
              </Button>
            </ButtonContainer>
          </StepGrid>
        )}

        {currentStep === 3 && (
          <StepGrid>
            <InputDiv>
              <Label style={codeError ? { color: 'red' } : {}}>
                Código
                {!isFashionTenant && <span style={{ color: 'red', marginLeft: 2 }}>*</span>}
                {codeError && (
                  <span style={{ fontSize: '12px', color: 'red', marginLeft: 6, fontWeight: 400 }}>
                    O código é obrigatório!
                  </span>
                )}
              </Label>
              <div style={codeError ? { border: '1px solid red', borderRadius: '2rem', overflow: 'hidden' } : {}}>
                <InfiniteSelect
                  mode="crud"
                  value={formData.code ? { id: formData.code, name: formData.code } : null}
                  onSelect={option => {
                    handleInputChange('code', option ? option.name : '')
                    setCodeError(false)
                  }}
                  fetchFunction={async (limit, offset, search?) => {
                    const response = await ApiService.get('/backend/code', { type: 'resource' })
                    const items = Array.isArray(response?.data) ? response.data : []
                    const mapped = items.map((c: any) => {
                      if (typeof c === 'string') return { id: c, name: c }
                      return { id: c.id || c.code, name: c.code || '' }
                    })
                    return search
                      ? mapped.filter((item: any) => item.name?.toLowerCase().includes(search.toLowerCase()))
                      : mapped
                  }}
                  onItemCreate={async (name: string) => {
                    return { id: name.trim(), name: name.trim() } as any
                  }}
                  onItemDelete={async (code: any) => {
                    await ApiService.code.delete(code)
                  }}
                  placeholder="Buscar ou criar Código"
                  allowCreate={true}
                  allowDelete={true}
                  showCreateOnlyIfNotExists={true}
                  minCreateLength={2}
                />
              </div>
            </InputDiv>

            <InputDiv>
              <Label>NCM</Label>
              <InfiniteSelect
                mode="crud"
                value={selectedNcm}
                onSelect={option => setSelectedNcm(option)}
                fetchFunction={async (limit, offset, search?) => {
                  const response = await ApiService.stockLookup.getNcms()
                  const items = response.data || []
                  const filtered = search
                    ? items.filter((m: any) => m.name?.toLowerCase().includes(search.toLowerCase()))
                    : items
                  return filtered.map((m: any) => ({ id: m.id, name: m.name }))
                }}
                onItemCreate={async (name: string) => {
                  const response = await ApiService.stockLookup.createNcm({ name })
                  return response.data
                }}
                onItemDelete={async (ncmId: any) => {
                  await ApiService.stockLookup.deleteNcm(ncmId)
                }}
                placeholder="Buscar ou criar NCM"
                allowCreate={true}
                allowDelete={true}
                showCreateOnlyIfNotExists={true}
                minCreateLength={2}
              />
            </InputDiv>

            <InputDiv style={{ gridColumn: '1 / -1' }}>
              <FileBlock>
                <FileBlockDiv>
                  <FileHeaderText>Nota fiscal</FileHeaderText>
                  {invoiceFiles.length === 0 ? (
                    <FileInputDiv>
                      <FileInputLabel htmlFor='invoice-upload'>
                        Procurar
                        <FileInputHidden
                          id='invoice-upload'
                          type='file'
                          multiple
                          accept='application/pdf,image/*'
                          onChange={handleUploadInvoice}
                        />
                      </FileInputLabel>
                      <FileFirstText>ou arraste um arquivo até aqui</FileFirstText>
                      <FileSecondText>Apenas arquivos .pdf de no máximo 10mb</FileSecondText>
                    </FileInputDiv>
                  ) : (
                    <>
                      <FilesInfoDiv>
                        {invoiceFiles.map((file, index) => (
                          <FileInfoDiv key={index}>
                            <FileInfoImg src={fileIcon} alt='arquivo' />
                            <FileMiddleDiv>
                              <FileUpperMiddleDiv>
                                <FileName>{file.name}</FileName>
                              </FileUpperMiddleDiv>
                              <FileProgressContainer>
                                <FileProgressFill />
                              </FileProgressContainer>
                            </FileMiddleDiv>
                            <FileInfoCancelImg
                              src={cancelIcon}
                              alt='remover'
                              onClick={() => handleRemoveInvoice(index)}
                            />
                          </FileInfoDiv>
                        ))}
                      </FilesInfoDiv>
                      <FileInputLabel htmlFor='invoice-upload' className='down'>
                        Procurar
                        <FileInputHidden
                          id='invoice-upload'
                          type='file'
                          multiple
                          accept='application/pdf,image/*'
                          onChange={handleUploadInvoice}
                        />
                      </FileInputLabel>
                    </>
                  )}
                </FileBlockDiv>
              </FileBlock>
            </InputDiv>

            <ButtonContainer>
              <Button type='button' onClick={handleBack} className='cancel'>
                <ChevronLeft size={16} /> Voltar
              </Button>
              <Button type='button' onClick={onSubmit}>
                {variation ? '+ Criar Variação' : id ? 'Alterar' : `+ Adicionar ${itemName}`}
              </Button>
            </ButtonContainer>
          </StepGrid>
        )}
      </ContainerOfModal>
    </ModalTest>
  )
}

export default CreateResourceModal
